/**
 * Loader ⇄ frame protocol: message shapes, validation and the geometry state
 * machine. Pure: no DOM, no React — shared by the host-side loader (must stay
 * tiny) and the in-frame bridge. See docs/superpowers/specs/2026-09-29-iframe-loader-design.md §4.
 */
export const PROTOCOL_VERSION = 1 as const;
export const MOBILE_BREAKPOINT = 800; // matches $mobile-view-breakpoint in src/scss/variables.scss
export const CLOSED_MARGIN = 8;
export const READY_TIMEOUT_MS = 5000;

export type FrameEventName =
    | 'ready'
    | 'chatOpen'
    | 'chatClose'
    | 'chatVisible'
    | 'chatHidden'
    | 'error';
export type GeometryMode = 'fixed' | 'viewport';
export type CommandName = 'open' | 'close' | 'toggle';

export type FrameToLoader =
    | { cui: 1; type: 'ready'; variant: string; version: string }
    | { cui: 1; type: 'geometry'; mode: GeometryMode; width: number; height: number }
    | { cui: 1; type: 'state'; open: boolean; visible: boolean; fullScreen: boolean }
    | { cui: 1; type: 'event'; name: FrameEventName; data?: unknown };

export type LoaderToFrame =
    | {
          cui: 1;
          type: 'init';
          hostConfig: Record<string, unknown>;
          pageUrl: string;
          hostOrigin: string;
      }
    | { cui: 1; type: 'page'; url: string }
    | { cui: 1; type: 'command'; name: CommandName }
    | { cui: 1; type: 'viewport'; width: number; height: number; mobile: boolean };

export type FrameMessage = FrameToLoader | LoaderToFrame;

const EVENT_NAMES: FrameEventName[] = [
    'ready',
    'chatOpen',
    'chatClose',
    'chatVisible',
    'chatHidden',
    'error',
];
const COMMANDS: CommandName[] = ['open', 'close', 'toggle'];

const isObj = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === 'string';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

/** Validate an incoming postMessage payload. Returns null for anything not ours. */
export function parseFrameMessage(data: unknown): FrameMessage | null {
    if (!isObj(data) || data.cui !== PROTOCOL_VERSION || !isStr(data.type)) return null;
    const m = data;
    switch (m.type) {
        case 'ready':
            return isStr(m.variant) && isStr(m.version)
                ? { cui: 1, type: 'ready', variant: m.variant, version: m.version }
                : null;
        case 'geometry':
            return (m.mode === 'fixed' || m.mode === 'viewport') &&
                isNum(m.width) &&
                isNum(m.height)
                ? { cui: 1, type: 'geometry', mode: m.mode, width: m.width, height: m.height }
                : null;
        case 'state':
            return isBool(m.open) && isBool(m.visible) && isBool(m.fullScreen)
                ? {
                      cui: 1,
                      type: 'state',
                      open: m.open,
                      visible: m.visible,
                      fullScreen: m.fullScreen,
                  }
                : null;
        case 'event':
            return isStr(m.name) && (EVENT_NAMES as string[]).includes(m.name)
                ? { cui: 1, type: 'event', name: m.name as FrameEventName, data: m.data }
                : null;
        case 'init':
            return isObj(m.hostConfig) && isStr(m.pageUrl) && isStr(m.hostOrigin)
                ? {
                      cui: 1,
                      type: 'init',
                      hostConfig: m.hostConfig,
                      pageUrl: m.pageUrl,
                      hostOrigin: m.hostOrigin,
                  }
                : null;
        case 'page':
            return isStr(m.url) ? { cui: 1, type: 'page', url: m.url } : null;
        case 'command':
            return isStr(m.name) && (COMMANDS as string[]).includes(m.name)
                ? { cui: 1, type: 'command', name: m.name as CommandName }
                : null;
        case 'viewport':
            return isNum(m.width) && isNum(m.height) && isBool(m.mobile)
                ? { cui: 1, type: 'viewport', width: m.width, height: m.height, mobile: m.mobile }
                : null;
        default:
            return null;
    }
}

export type Rect = { left: number; top: number; width: number; height: number };
export type WidgetState = {
    open: boolean;
    visible: boolean;
    fullScreen: boolean;
    overlayMode: boolean;
    bubbleVisible: boolean;
};
export type MeasuredRects = {
    launcher: Rect | null;
    badge: Rect | null;
    bubble: Rect | null;
    conversation: Rect | null;
};
export type Geometry = { mode: GeometryMode; width: number; height: number };

function union(rects: (Rect | null)[]): Rect | null {
    const present = rects.filter((r): r is Rect => !!r && r.width > 0 && r.height > 0);
    if (present.length === 0) return null;
    const left = Math.min(...present.map((r) => r.left));
    const top = Math.min(...present.map((r) => r.top));
    const right = Math.max(...present.map((r) => r.left + r.width));
    const bottom = Math.max(...present.map((r) => r.top + r.height));
    return { left, top, width: right - left, height: bottom - top };
}

/**
 * Geometry state machine (spec §4). The iframe is bottom-right anchored, so
 * only width/height matter for `fixed`; `viewport` means inset 0.
 *
 * `rects` are in the frame's own coordinates and `frame` is the frame's own
 * inner size. Because the iframe is anchored to the host's bottom-right
 * corner, a `fixed` box must reach from the union box's left/top edge all the
 * way to the frame's right/bottom edge — the launcher sits 12–20 px in from
 * the corner, and sizing to the union's bare extent would clip it (and any
 * bubble) on the left/top.
 */
export function computeGeometry(
    state: WidgetState,
    rects: MeasuredRects,
    viewport: { width: number; height: number },
    frame: { width: number; height: number }
): Geometry {
    if (!state.visible) return { mode: 'fixed', width: 0, height: 0 };
    if (
        state.open &&
        (state.overlayMode || state.fullScreen || viewport.width < MOBILE_BREAKPOINT)
    ) {
        return { mode: 'viewport', width: viewport.width, height: viewport.height };
    }
    let box: Rect | null;
    let margin = 0;
    if (state.open) {
        box = union([rects.launcher, rects.conversation]);
    } else if (state.bubbleVisible) {
        box = union([rects.launcher, rects.bubble]);
    } else {
        box = union([rects.launcher, rects.badge]);
        margin = CLOSED_MARGIN;
    }
    if (!box) return { mode: 'fixed', width: 0, height: 0 };
    const width = Math.min(
        viewport.width,
        Math.max(0, Math.round(frame.width - box.left + margin))
    );
    const height = Math.min(
        viewport.height,
        Math.max(0, Math.round(frame.height - box.top + margin))
    );
    return { mode: 'fixed', width, height };
}

const STRIPPED_KEYS = ['frameBaseUrl', 'mountTo', 'embedded'];

/** Host config crosses the origin boundary as data: no functions, no frame-breaking keys. */
export function sanitizeHostConfig(cfg: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(cfg)) {
        if (STRIPPED_KEYS.includes(key) || typeof value === 'function') continue;
        out[key] = isObj(value) ? sanitizeHostConfig(value) : value;
    }
    return out;
}
